import React, { useState } from "react";
import { Mail, MapPin, Network, Building2, Target, ShieldCheck, GraduationCap, LineChart, Quote } from "lucide-react";
import Reveal from "@/components/Reveal";
import { Eyebrow, BookButton } from "@/components/Bits";
import { CONTACT_EMAIL, LOCATION } from "@/lib/site";

const HEADSHOT_URL =
  "https://customer-assets.emergentagent.com/job_org-signals-research/artifacts/ks606qqh_Megan.JPG";

const FounderPhoto = () => {
  const [err, setErr] = useState(false);
  if (err) {
    return (
      <div className="aspect-[3/4] w-full bg-ink-deep flex items-center justify-center">
        <span className="font-serif text-6xl text-brand-light">MAT</span>
      </div>
    );
  }
  return (
    <img
      src={HEADSHOT_URL}
      onError={() => setErr(true)}
      alt="Megan Adair Titcomb"
      className="aspect-[3/4] w-full object-cover object-top"
    />
  );
};

const principles = [
  { icon: Network, t: "Operational Integrity Over Hype", b: "Culture isn’t about soft perks or mission statements; it reflects structural mechanics — how decisions are made, how information flows, and how accountability is distributed." },
  { icon: Building2, t: "Organizational Architecture", b: "We view companies as complex, engineered systems. The structural design of your workflows determines your capacity for growth, transition, and eventual integration." },
  { icon: Target, t: "Contextual Specificity", b: "The Canadian lower-middle market has its own dynamics. Our frameworks are calibrated to the regional governance, tax realities, and cultural conditions of Canadian owners." },
];

const AboutPage = () => {
  return (
    <main data-testid="about-page" className="pt-20">
      {/* HERO */}
      <section className="bg-cream py-20 md:py-28 relative">
        <div className="grid-lines" />
        <div className="max-w-7xl mx-auto px-5 md:px-10 relative z-10">
          <div className="max-w-3xl">
            <Eyebrow>About Cultural Capital</Eyebrow>
            <h1 className="font-serif text-4xl md:text-6xl leading-[1.08] text-ink-deep">
              A new layer of <span className="italic text-brand">transaction intelligence.</span>
            </h1>
            <p className="mt-7 text-xl text-[#475569]">
              We evaluate a company’s operational and human integrity with the same precision as its financial
              statements — so the true value built by founders is made visible, protected, and seamlessly transferred.
            </p>
          </div>
        </div>
      </section>

      {/* MISSION & VISION */}
      <section className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10 grid md:grid-cols-2 gap-14">
          <Reveal>
            <Eyebrow>Our Mission</Eyebrow>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-ink-deep">
              To establish a new layer of transaction intelligence where a company’s operational and human integrity
              is evaluated with the same precision as its financial statements.
            </p>
          </Reveal>
          <Reveal delay={120}>
            <Eyebrow>Our Vision</Eyebrow>
            <p className="font-serif text-2xl md:text-3xl leading-relaxed text-ink-deep">
              To ensure that when a business changes hands, the true value built by its founders is made visible,
              protected, and seamlessly transferred to the next generation of leadership.
            </p>
          </Reveal>
        </div>
      </section>

      {/* WHY CULTURAL CAPITAL */}
      <section className="bg-ink-deep py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5">
              <Eyebrow light>Why “Cultural Capital”?</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-white leading-tight">
                Every business is evaluated through two lenses.
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-[#CBD5E1] mb-6">
                The first lens is financial: balance sheets, EBITDA, tax structures, and cash flow. Traditional
                advisors have mastered this layer. But there is a second, invisible layer that dictates whether a
                business can actually survive a change in control.
              </p>
              <blockquote className="border-l-2 border-brand pl-6 py-1">
                <Quote className="w-7 h-7 text-brand mb-3" />
                <p className="font-serif text-xl md:text-2xl text-white italic">
                  Cultural Capital is the collective trust, institutional knowledge, customer goodwill, unwritten
                  habits, reputation, and authority structures that keep an owner-led business moving.
                </p>
              </blockquote>
              <p className="text-[#94A3B8] mt-6">
                If this capital is trapped entirely inside the founder’s head or personal relationships, the business
                becomes fragile. We founded this company to bridge the gap between cold financial analysis and the
                living, breathing human systems that actually generate revenue.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CORE PRINCIPLES */}
      <section className="bg-cream py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="max-w-2xl mb-14">
            <Eyebrow>Core Principles</Eyebrow>
            <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">How we approach the work.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {principles.map((p, i) => (
              <Reveal key={i} delay={i * 100} className="bg-white border border-[#E2E8F0] p-8 hover:border-brand transition-colors">
                <p.icon className="w-8 h-8 text-brand mb-6" />
                <h3 className="font-serif text-xl text-ink-deep mb-3">{p.t}</h3>
                <p className="text-[#475569]">{p.b}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* LEADERSHIP */}
      <section data-testid="leadership-section" className="bg-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-5 md:px-10">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-4">
              <div className="border border-[#E2E8F0] overflow-hidden">
                <FounderPhoto />
              </div>
              <div className="mt-5 space-y-3">
                <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-center gap-3 text-ink hover:text-brand transition-colors">
                  <Mail className="w-4 h-4" /> <span className="text-sm">{CONTACT_EMAIL}</span>
                </a>
                <p className="flex items-center gap-3 text-[#475569]">
                  <MapPin className="w-4 h-4" /> <span className="text-sm">{LOCATION}</span>
                </p>
              </div>
            </div>
            <div className="lg:col-span-8">
              <Eyebrow>Leadership</Eyebrow>
              <h2 className="font-serif text-3xl md:text-4xl text-ink-deep">Megan Adair Titcomb</h2>
              <p className="text-brand font-medium mb-6">Founder &amp; Principal Strategist</p>

              <div className="grid sm:grid-cols-2 gap-4 mb-8">
                <div className="flex items-start gap-3 bg-cream border border-[#E2E8F0] p-5">
                  <LineChart className="w-6 h-6 text-brand shrink-0" />
                  <p className="text-sm text-[#475569]"><span className="font-semibold text-ink">Financial Data.</span> Former Financial Data Specialist, Canada Overseas Investments Limited.</p>
                </div>
                <div className="flex items-start gap-3 bg-cream border border-[#E2E8F0] p-5">
                  <GraduationCap className="w-6 h-6 text-brand shrink-0" />
                  <p className="text-sm text-[#475569]"><span className="font-semibold text-ink">Organizational Psychology.</span> BA with Distinction, Psychology &amp; Political Science, University of Alberta.</p>
                </div>
              </div>

              <div className="space-y-4 text-[#475569]">
                <p>
                  Megan operates at the unique intersection of financial data, organizational systems, and strategic
                  business transitions. Her career has been dedicated to decoding how high-level numbers translate
                  into real-world operational execution.
                </p>
                <p>
                  Recognizing that spreadsheets alone often fail to capture critical transition liabilities, she also
                  founded Stella Polaris Canada, an advisory focused on organizational architecture. By blending her
                  background in asset data with a deep understanding of human systems, Megan provides owners and
                  acquirers the objective, multi-dimensional insight required to execute flawless transitions.
                </p>
                <p className="text-ink font-medium">
                  Director, BGC (Boys and Girls Club) of East Scarborough.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDEPENDENCE BAND */}
      <section className="bg-ink-deep py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-5 md:px-10 text-center">
          <div className="flex justify-center mb-6"><ShieldCheck className="w-10 h-10 text-brand" /></div>
          <h2 className="font-serif text-3xl md:text-4xl text-white mb-5">Fixed-Fee. Conflict-Free. Independent.</h2>
          <p className="text-lg text-[#94A3B8] mb-9">
            We sit entirely before the transaction or the formal estate plan. Because we do not accept brokerage
            commissions, sell financial products, or draft legal documents, our insights remain entirely objective —
            dedicated solely to protecting your equity and capital.
          </p>
          <BookButton label="Let’s Start a Conversation" variant="light" testId="about-cta" />
        </div>
      </section>
    </main>
  );
};

export default AboutPage;
